/**
 * 화면 확대/축소 — **앱 전체가 한 배율**(사용자 결정 2026-10-10).
 *
 * 헤더 조절자 · Ctrl+휠(어디서든) · Ctrl+= / Ctrl+- / Ctrl+0 이 이 배율 하나를 바꾸고, 헤더·창(모달)·패널·확인 대화상자·
 * 알림·분리한 창이 함께 커진다. 편집기·그리드·트리 글자 크기(설정 › 각 영역)는 「기본값」이고 배율이 그 위에 곱해진다.
 * 예전에는 본문(.content-area)만 CSS zoom 으로 확대되고 Ctrl+휠은 영역 글자 크기를 바꿨다.
 *
 * 거는 방법은 둘이다.
 *   스탠드얼론 — Electron 창 확대(webContents.setZoomFactor, main.js 「화면 확대/축소」). 브라우저 확대와 같아 좌표 문제가 없다.
 *   웹        — 페이지 최상위(html)에 CSS zoom. 앱이 브라우저 확대를 대신 누를 수 없어서다(사용자 결정: 웹도 헤더 조절자로).
 *               CSS zoom 아래에서는 두 가지를 지켜야 한다(실측 2026-10-10, Chromium 130):
 *     ① vh·vw 도 배율만큼 커진다 → 화면이 넘친다. CSS 에서 `Nvh` 대신 `calc(Nvh / var(--app-zoom, 1))` 로 쓴다
 *        (`--app-zoom` 은 여기서 html 에 건다. 스탠드얼론은 늘 1). `utils/viewportUnits.test.js` 가 날것의 vh·vw 를 막는다.
 *     ② 마우스 좌표(clientX)·getBoundingClientRect 는 화면 좌표인데 style 의 left/top/width 는 배율이 곱해진다 →
 *        마우스 위치에 띄우는 메뉴는 `menuPoint()`, 끌어서 옮기는 거리는 `toCssPx()` 로 바꿔 쓴다. 같은 화면 좌표끼리의
 *        비율(rect 대비 clientX)은 그대로 써도 된다.
 */
import { loadSection, saveSection } from './appSettings';

export const ZOOM_MIN = 50;
export const ZOOM_MAX = 200;
export const ZOOM_STEP = 10;
export const ZOOM_DEFAULT = 100;
// 휠 한 칸(deltaY 100)에 한 단계. 터치패드 핀치는 작은 값이 여러 번 오므로 모아서 넘긴다
const WHEEL_THRESHOLD = 100;

export const clampZoom = (v) => {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return ZOOM_DEFAULT;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, n));
};

let current = ZOOM_DEFAULT;
try { current = clampZoom(loadSection('uiZoom')); } catch (_e) { current = ZOOM_DEFAULT; }
const listeners = new Set();
let attached = false;

function electronZoom() {
  try { return (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.zoom) || null; } catch (_e) { return null; }
}

/** 창 확대(Electron)로 거는가 — 아니면 CSS zoom(웹) */
export function nativeZoom() { return !!electronZoom(); }

/** 지금 배율(%) */
export function getZoom() { return current; }

const docs = new Set();

function applyTo(doc) {
  try {
    const el = doc.documentElement;
    const css = !electronZoom() && current !== 100;
    el.style.zoom = css ? String(current / 100) : '';
    if (css) el.style.setProperty('--app-zoom', String(current / 100));
    else el.style.removeProperty('--app-zoom');
  } catch (_e) { /* 닫힌 창 */ }
}

function apply() {
  const ez = electronZoom();
  if (ez) { try { ez.set(current / 100); } catch (_e) { /* main 이 없어도 앱은 돈다 */ } }
  docs.forEach(applyTo);
}

/** CSS zoom 배율(1 = 보정 불필요). 스탠드얼론(창 확대)·100% 에서는 1 */
export function cssZoomFactor() {
  return electronZoom() ? 1 : current / 100;
}

/** 화면 좌표(clientX·rect·innerWidth) → CSS px. 끌어서 옮긴 거리·마우스 위치를 style 에 쓸 때 */
export function toCssPx(v) {
  return v / cssZoomFactor();
}

/**
 * 마우스 위치에 띄우는 메뉴의 자리(CSS px) — 창 밖으로 나가지 않게 메뉴 크기(CSS px)만큼 당긴다.
 * @param {{clientX:number, clientY:number}} e @param {number} w @param {number} h
 */
export function menuPoint(e, w = 0, h = 0, win = typeof window !== 'undefined' ? window : null) {
  const vw = win ? toCssPx(win.innerWidth) : Infinity;
  const vh = win ? toCssPx(win.innerHeight) : Infinity;
  return {
    x: Math.max(0, Math.min(toCssPx(e.clientX), vw - w)),
    y: Math.max(0, Math.min(toCssPx(e.clientY), vh - h)),
  };
}

/** 배율을 바꾼다 — 저장하고, 창에 걸고, 화면(헤더 표시)에 알린다 */
export function setZoom(v) {
  const next = clampZoom(v);
  if (next !== current) {
    current = next;
    try { saveSection('uiZoom', current); } catch (_e) { /* 저장 불가 환경 */ }
    listeners.forEach((fn) => { try { fn(current); } catch (_e) { /* 구독자 실패가 확대를 막지 않게 */ } });
  }
  apply();
  return current;
}
export const zoomIn = () => setZoom(current + ZOOM_STEP);
export const zoomOut = () => setZoom(current - ZOOM_STEP);
export const resetZoom = () => setZoom(ZOOM_DEFAULT);

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Ctrl+= / Ctrl++ / Ctrl+- / Ctrl+0 → 'in' | 'out' | 'reset' | null. 순수 판정이라 테스트한다 */
export function zoomKeyAction(e) {
  // Shift 는 「+」(미국 자판의 Shift+=)일 때만 허용한다 — Ctrl+Shift+- 같은 다른 단축키를 먹지 않게
  if (!e || !(e.ctrlKey || e.metaKey) || e.altKey || (e.shiftKey && e.key !== '+')) return null;
  const k = e.key;
  const c = e.code;
  if (k === '=' || k === '+' || c === 'Equal' || c === 'NumpadAdd') return 'in';
  if (k === '-' || c === 'Minus' || c === 'NumpadSubtract') return 'out';
  if (k === '0' || c === 'Digit0' || c === 'Numpad0') return 'reset';
  return null;
}

/** Ctrl+휠 누적 → 단계(+1 확대 / -1 축소 / 0 아직). 순수 함수 */
export function wheelStep(acc, deltaY) {
  const next = acc + deltaY;
  if (Math.abs(next) < WHEEL_THRESHOLD) return { acc: next, step: 0 };
  return { acc: 0, step: next > 0 ? -1 : 1 };
}

/**
 * 앱 시작 — 저장된 배율을 걸고 Ctrl+휠·단축키를 받는다.
 * 분리한 창은 그 창의 문서에 따로 붙인다(`attachDocument` — 웹은 그 창에도 CSS zoom 을 건다).
 */
export function initAppZoom() {
  if (attached) return;
  attached = true;
  attachDocument(document);
  apply();
}

/** 문서 하나에 배율을 걸고 Ctrl+휠·단축키를 붙인다. 돌려주는 함수로 뗀다 */
export function attachDocument(doc) {
  if (!doc || docs.has(doc)) return () => {};
  docs.add(doc);
  applyTo(doc);
  let acc = 0;
  const onWheel = (e) => {
    if (!e.ctrlKey) return;
    // 기본 동작을 막는다 — 앱 배율 하나만 움직여야 한다(영역 글자 크기를 바꾸던 예전 동작은 없앴다)
    e.preventDefault();
    const r = wheelStep(acc, e.deltaY);
    acc = r.acc;
    if (r.step) setZoom(current + r.step * ZOOM_STEP);
  };
  const onKey = (e) => {
    const act = zoomKeyAction(e);
    if (!act) return;
    e.preventDefault();
    e.stopPropagation();
    if (act === 'in') zoomIn();
    else if (act === 'out') zoomOut();
    else resetZoom();
  };
  doc.addEventListener('wheel', onWheel, { passive: false, capture: true });
  doc.addEventListener('keydown', onKey, true);
  return () => {
    try {
      doc.removeEventListener('wheel', onWheel, { capture: true });
      doc.removeEventListener('keydown', onKey, true);
    } catch (_e) { /* 이미 닫힌 창 */ }
    docs.delete(doc);
  };
}
